import { useState } from "react";
import api from "@/lib/api";
import { useBranch } from "@/context/BranchContext";
import { useRefresh } from "@/hooks/useRefresh";
import { peso, num } from "@/lib/format";
import { MovementDialog } from "@/components/MovementDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus } from "lucide-react";

const STATUS = {
  ok: <Badge className="bg-green-100 text-green-800 hover:bg-green-100">OK</Badge>,
  low: <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Low</Badge>,
  out: <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Out</Badge>,
};

export default function Stock() {
  const { selected } = useBranch();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  useRefresh(() => api.get("/stock", { params: { branch_id: selected } }).then((r) => setRows(r.data)), [selected]);

  const filtered = rows.filter((r) => (r.item_name + r.category + r.branch_name).toLowerCase().includes(search.toLowerCase()));
  const totalValue = filtered.reduce((s, r) => s + r.value, 0);

  return (
    <div className="space-y-4" data-testid="stock-page">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Stock on hand</h1>
          <p className="text-sm text-muted-foreground">{filtered.length} lines · {peso(totalValue)} total value</p>
        </div>
        <div className="flex gap-2">
          <Input placeholder="Search…" className="w-44 sm:w-64" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="stock-search-input" />
          <Button onClick={() => setOpen(true)} data-testid="record-movement-btn"><Plus className="h-4 w-4 mr-1" /> Record</Button>
        </div>
      </div>
      <div className="border rounded-xl bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-muted-foreground text-left">
            <tr className="border-b">
              <th className="py-2 px-3">Item</th><th className="py-2 px-3">Branch</th><th className="py-2 px-3">Category</th>
              <th className="py-2 px-3 text-right">Qty</th><th className="py-2 px-3 text-right">Reorder pt</th>
              <th className="py-2 px-3 text-right">Value (net)</th><th className="py-2 px-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.stock_id} className="border-b last:border-0 hover:bg-slate-50">
                <td className="py-2 px-3 font-medium">{r.item_name}</td>
                <td className="py-2 px-3 text-muted-foreground">{r.branch_name}</td>
                <td className="py-2 px-3">{r.category}</td>
                <td className="py-2 px-3 text-right font-medium">{num(r.qty)} {r.unit}</td>
                <td className="py-2 px-3 text-right">{num(r.reorder_point)}</td>
                <td className="py-2 px-3 text-right">{peso(r.value)}</td>
                <td className="py-2 px-3">{STATUS[r.status]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <MovementDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
